/**
 * Account e ruoli: forma dei dati e aiuti puri per V49, V51 e V44.
 *
 * Le regole vere le applica il server; qui si ricavano solo i raggruppamenti
 * e le opzioni da mostrare, cosi da non proporre un'assegnazione che il
 * server rifiuterebbe comunque.
 */

import { formatoIstante } from '../comuni/date.js';
import type { SquadraStagione } from '../navigazione/contesto.js';

export type RuoloAssegnabile = 'mister' | 'dirigente';
export type RuoloAccount = 'admin_squadra' | RuoloAssegnabile;

export const ETICHETTA_RUOLO: Record<RuoloAccount, string> = {
  admin_squadra: 'Admin Squadra',
  mister: 'Mister',
  dirigente: 'Dirigente',
};

export interface RuoloAssegnato {
  id: string;
  ruolo: RuoloAccount;
  squadraStagioneId: string | null;
  squadra: string | null;
  categoria: string | null;
  stagione: string | null;
  stagioneCorrente: boolean;
}

export interface AccountSocieta {
  id: string;
  nome: string;
  cognome: string;
  email: string;
  attivo: boolean;
  ultimoAccesso: string | null;
  /** L'invito ancora da accettare, null per chi ha gia scelto la password. */
  invito: { scadeIl: string; scaduto: boolean } | null;
  ruoli: RuoloAssegnato[];
}

export interface ElencoAccount {
  account: AccountSocieta[];
  limite: { usato: number; massimo: number };
}

export const eAdmin = (a: AccountSocieta): boolean =>
  a.ruoli.some((r) => r.ruolo === 'admin_squadra');

/** Ruoli di squadra per stagione, nell'ordine in cui li manda il server. */
export function ruoliPerStagione(
  ruoli: readonly RuoloAssegnato[],
): { stagione: string; corrente: boolean; ruoli: RuoloAssegnato[] }[] {
  const gruppi: { stagione: string; corrente: boolean; ruoli: RuoloAssegnato[] }[] = [];
  for (const r of ruoli) {
    if (!r.stagione) continue;
    let gruppo = gruppi.find((g) => g.stagione === r.stagione);
    if (!gruppo) {
      gruppo = { stagione: r.stagione, corrente: r.stagioneCorrente, ruoli: [] };
      gruppi.push(gruppo);
    }
    gruppo.ruoli.push(r);
  }
  return gruppi;
}

/**
 * Squadre-stagione su cui l'account puo ricevere il ruolo: fuori quelle dove
 * ha gia un ruolo di squadra, perche Mister e Dirigente non si sommano e lo
 * stesso ruolo non si ripete.
 */
export function squadreAssegnabili(
  account: AccountSocieta,
  squadre: readonly SquadraStagione[],
): SquadraStagione[] {
  const occupate = new Set(account.ruoli.map((r) => r.squadraStagioneId));
  return squadre.filter((s) => !occupate.has(s.id));
}

/**
 * Squadre-stagione proposte a una riga dell'invito (V50): fuori quelle gia
 * scelte nelle altre righe, perche sulla stessa squadra-stagione un account
 * ha un ruolo solo.
 */
export function squadreLibereInvito(
  scelte: readonly string[],
  riga: number,
  squadre: readonly SquadraStagione[],
): SquadraStagione[] {
  const altrove = new Set(scelte.filter((s, i) => i !== riga && s));
  return squadre.filter((s) => !altrove.has(s.id));
}

const formatoScadenza = formatoIstante({ day: 'numeric', month: 'long' });

/** "fino al 23 settembre", o "scaduto il 23 settembre". */
export function statoInvito(invito: NonNullable<AccountSocieta['invito']>): string {
  const giorno = formatoScadenza.format(new Date(invito.scadeIl));
  return invito.scaduto ? `Invito scaduto il ${giorno}` : `Invito valido fino al ${giorno}`;
}

/** Staff di una squadra-stagione (V44): Mister prima dei Dirigenti, poi per cognome. */
export function staffDi(
  elenco: readonly AccountSocieta[],
  squadraStagioneId: string,
): { account: AccountSocieta; ruolo: RuoloAssegnato }[] {
  const staff = elenco.flatMap((account) =>
    account.ruoli
      .filter((r) => r.squadraStagioneId === squadraStagioneId)
      .map((ruolo) => ({ account, ruolo })),
  );
  const peso = (r: RuoloAccount) => (r === 'mister' ? 0 : 1);
  return staff.sort(
    (a, b) =>
      peso(a.ruolo.ruolo) - peso(b.ruolo.ruolo) ||
      a.account.cognome.localeCompare(b.account.cognome, 'it') ||
      a.account.nome.localeCompare(b.account.nome, 'it'),
  );
}

/** Account attivi che possono entrare nello staff di questa squadra-stagione. */
export const candidatiStaff = (
  elenco: readonly AccountSocieta[],
  squadraStagioneId: string,
): AccountSocieta[] =>
  elenco.filter((a) => a.attivo && !a.ruoli.some((r) => r.squadraStagioneId === squadraStagioneId));

export const nomeSquadra = (s: { squadra: string | null; categoria: string | null }): string =>
  s.categoria && s.categoria !== s.squadra ? `${s.squadra} · ${s.categoria}` : (s.squadra ?? '');
